import React, { useRef, useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Circle, X, Undo2, Trash2 } from "lucide-react";

// Car diagram image with new layout:
// Left column: Top view (vista dall'alto)
// Center-top: Front view + Right side view
// Center-bottom: Rear view + Left side view
// Right column: Seats (sedili) - 3 rear seats top, 2 front seats bottom
const CAR_IMAGE_URL = "/car-diagram.png";

// Function to convert damage coordinates to vehicle zone description
// New layout based on uploaded image:
// Left (0-15%): Top view
// Center (15-75%): 
//   Top half (0-50%): Front view (15-40%) + Right side (40-75%)
//   Bottom half (50-100%): Rear view (15-40%) + Left side (40-75%)
// Right (75-100%): Seats - 3 rear (top), 2 front (bottom)
export const getDamageZone = (x, y, canvasWidth = 800, canvasHeight = 600) => {
  const relX = x / canvasWidth;
  const relY = y / canvasHeight;
  
  let view = "";
  let position = "";
  
  // Left column - Top view (vista dall'alto)
  if (relX < 0.15) {
    view = "Vista dall'Alto";
    if (relY < 0.2) position = "anteriore";
    else if (relY > 0.8) position = "posteriore";
    else if (relY < 0.4) position = "cofano";
    else if (relY > 0.6) position = "bagagliaio";
    else position = "tetto";
  }
  // Right column - Seats (sedili)
  else if (relX > 0.75) {
    if (relY < 0.5) {
      // Top - 3 rear seats (sedili posteriori)
      view = "Sedili Posteriori";
      const seatX = (relX - 0.75) / 0.25;
      if (seatX < 0.33) position = "sinistro";
      else if (seatX > 0.66) position = "destro";
      else position = "centrale";
    } else {
      // Bottom - 2 front seats (sedili anteriori)
      view = "Sedili Anteriori";
      const seatX = (relX - 0.75) / 0.25;
      if (seatX < 0.5) position = "guidatore";
      else position = "passeggero";
    }
  }
  // Center area
  else {
    const centerX = (relX - 0.15) / 0.60; // Normalize to 0-1 within center
    
    if (relY < 0.5) {
      // Top row: Front view (left) + Right side view (right)
      if (centerX < 0.4) {
        view = "Vista Frontale";
        const localX = centerX / 0.4;
        if (relY < 0.15) position = "tetto";
        else if (relY > 0.4) position = "paraurti";
        else if (localX < 0.35) position = "lato sinistro";
        else if (localX > 0.65) position = "lato destro";
        else position = "griglia/cofano";
      } else {
        view = "Fiancata Destra";
        const localX = (centerX - 0.4) / 0.6;
        if (localX < 0.25) position = "anteriore";
        else if (localX > 0.75) position = "posteriore";
        else if (localX < 0.45) position = "portiera anteriore";
        else if (localX > 0.55) position = "portiera posteriore";
        else position = "montante centrale";
      }
    } else {
      // Bottom row: Rear view (left) + Left side view (right)
      if (centerX < 0.4) {
        view = "Vista Posteriore";
        const localX = centerX / 0.4;
        if (relY < 0.55) position = "lunotto";
        else if (relY > 0.9) position = "paraurti";
        else if (localX < 0.35) position = "lato sinistro";
        else if (localX > 0.65) position = "lato destro";
        else position = "portellone";
      } else {
        view = "Fiancata Sinistra";
        const localX = (centerX - 0.4) / 0.6;
        if (localX < 0.25) position = "posteriore";
        else if (localX > 0.75) position = "anteriore";
        else if (localX < 0.45) position = "portiera posteriore";
        else if (localX > 0.55) position = "portiera anteriore";
        else position = "montante centrale";
      }
    }
  }
  
  return { view, position };
};

// Function to convert all damage marks to a text description
export const getDamageDescription = (marks) => {
  if (!marks || marks.length === 0) return "";
  
  const damages = marks.map((mark, index) => {
    const zone = getDamageZone(mark.x, mark.y);
    const type = mark.type === "circle" ? "Ammaccatura" : "Graffio";
    return `${type} - ${zone.view}, ${zone.position}`;
  });
  
  // Group similar damages
  const grouped = {};
  damages.forEach(d => {
    grouped[d] = (grouped[d] || 0) + 1;
  });
  
  return Object.entries(grouped)
    .map(([desc, count]) => count > 1 ? `${desc} (x${count})` : desc)
    .join("; ");
};

export default function VehicleDamageMarker({ damageData, setDamageData, canvasRef: externalCanvasRef, readOnly = false }) {
  const internalCanvasRef = useRef(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const [tool, setTool] = useState("circle"); // "circle" for dent, "x" for scratch
  const [imageLoaded, setImageLoaded] = useState(false);
  const [marks, setMarks] = useState(damageData?.marks || []);
  const imageRef = useRef(null);

  // Update marks when damageData changes (for readOnly mode)
  useEffect(() => {
    if (damageData?.marks) {
      setMarks(damageData.marks);
    }
  }, [damageData]);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      redrawCanvas();
    };
    img.onerror = () => {
      console.error("Failed to load car image");
    };
    img.src = CAR_IMAGE_URL;
  }, []);

  useEffect(() => {
    if (imageLoaded) {
      redrawCanvas();
    }
  }, [marks, imageLoaded]);

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imageRef.current) return;

    const ctx = canvas.getContext("2d");
    const img = imageRef.current;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background image
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Draw marks
    marks.forEach((mark) => {
      ctx.strokeStyle = mark.type === "circle" ? "#ef4444" : "#f97316";
      ctx.lineWidth = 3;

      if (mark.type === "circle") {
        ctx.beginPath();
        ctx.arc(mark.x, mark.y, 12, 0, 2 * Math.PI);
        ctx.stroke();
      } else {
        // Draw X
        ctx.beginPath();
        ctx.moveTo(mark.x - 8, mark.y - 8);
        ctx.lineTo(mark.x + 8, mark.y + 8);
        ctx.moveTo(mark.x + 8, mark.y - 8);
        ctx.lineTo(mark.x - 8, mark.y + 8);
        ctx.stroke();
      }
    });
  };

  const handleCanvasClick = (e) => {
    if (readOnly) return; // Don't allow clicks in readOnly mode
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    const newMark = { x, y, type: tool };
    const newMarks = [...marks, newMark];
    setMarks(newMarks);
    setDamageData({ marks: newMarks });
  };

  const handleUndo = () => {
    if (readOnly) return;
    if (marks.length > 0) {
      const newMarks = marks.slice(0, -1);
      setMarks(newMarks);
      setDamageData({ marks: newMarks });
    }
  };

  const handleClear = () => {
    if (readOnly) return;
    setMarks([]);
    setDamageData({ marks: [] });
  };

  const totalDents = marks.filter(m => m.type === "circle").length;
  const totalScratches = marks.filter(m => m.type === "x").length;

  // In readOnly mode, just show the canvas with marks
  if (readOnly) {
    return (
      <div className="space-y-2" data-testid="vehicle-damage-marker-readonly">
        <canvas
          ref={canvasRef}
          width={800}
          height={600}
          className="w-full border border-zinc-700 rounded-lg cursor-default bg-zinc-900"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vehicle-damage-marker">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-zinc-400 text-sm">
          Clicca sull'immagine per segnare ammaccature (cerchio rosso) o graffi (X arancione)
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={tool === "circle" ? "default" : "outline"}
            size="sm"
            onClick={() => setTool("circle")}
            className={tool === "circle" ? "bg-red-500 hover:bg-red-600" : ""}
          >
            <Circle className="w-4 h-4 mr-1" />
            Ammaccatura
          </Button>
          <Button
            type="button"
            variant={tool === "x" ? "default" : "outline"}
            size="sm"
            onClick={() => setTool("x")}
            className={tool === "x" ? "bg-orange-500 hover:bg-orange-600" : ""}
          >
            <X className="w-4 h-4 mr-1" />
            Graffio
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleUndo}
            disabled={marks.length === 0}
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={marks.length === 0}
            className="text-red-400 hover:text-red-300"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="relative bg-white rounded-lg overflow-hidden border border-zinc-600">
        <canvas
          ref={canvasRef}
          width={800}
          height={600}
          onClick={handleCanvasClick}
          className="w-full cursor-crosshair"
          style={{ maxHeight: "450px" }}
        />
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-800">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}
      </div>

      {(totalDents > 0 || totalScratches > 0) && (
        <div className="p-3 bg-zinc-800/50 rounded-lg">
          <div className="text-sm text-zinc-300">
            <strong>Riepilogo danni:</strong>
            <span className="ml-2">
              {totalDents} ammaccature, {totalScratches} graffi
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-zinc-400">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full border-2 border-red-500"></span>
          <span>Ammaccatura ({totalDents})</span>
        </div>
        <div className="flex items-center gap-1">
          <X className="w-3 h-3 text-orange-500" />
          <span>Graffio ({totalScratches})</span>
        </div>
      </div>
    </div>
  );
}
